import FullscreenIcon from '@mui/icons-material/Fullscreen';
import { Box, IconButton, Typography } from '@mui/material';
import ListOfClubs from './ListOfClubs';
import { useNavigate } from 'react-router-dom';
import { club } from 'apis';
import { useQuery } from 'react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { GoogleMap, LoadScript, Marker, InfoWindow, Polyline } from '@react-google-maps/api';
import { useTranslation } from 'react-i18next';

const GREECE_CENTER = { lat: 39.0742, lng: 21.8243 };
const DEFAULT_GREECE_ZOOM = 7;

const containerStyle = {
    width: '100%',
    height: '100%'
};

const toValidCoordinatePair = (latValue: any, lngValue: any) => {
    const lat = latValue === null || latValue === '' ? NaN : Number(latValue);
    const lng = lngValue === null || lngValue === '' ? NaN : Number(lngValue);

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
    if (lat === 0 && lng === 0) return null;

    return { lat, lng };
};

const getGoogleMapsApiKey = () => {
    try {
        const config = window.CONFIG || JSON.parse(window.localStorage.getItem('CONFIG') || '{}');
        return config.VITE_GOOGLE_MAPS_API_KEY || '';
    } catch (error) {
        return '';
    }
};

const createSelectedMarkerIcon = () => {
    if (!window.google?.maps) return undefined;

    const width = 44;
    const height = 58;
    const dotRadius = 8;
    const fill = '#ff6f61';
    const svg = encodeURIComponent(`
        <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
            <path d="M${width / 2} 1C${width * 0.28} 1 ${width * 0.12} ${height * 0.22} ${width * 0.12} ${height * 0.41}C${width * 0.12} ${height * 0.69} ${width / 2} ${height - 2} ${width / 2} ${height - 2}C${width / 2} ${height - 2} ${width * 0.88} ${height * 0.69} ${width * 0.88} ${height * 0.41}C${width * 0.88} ${height * 0.22} ${width * 0.72} 1 ${width / 2} 1Z" fill="${fill}" stroke="#ffffff" stroke-width="2"/>
            <circle cx="${width / 2}" cy="${height * 0.36}" r="${dotRadius}" fill="#ffffff"/>
        </svg>
    `);

    return {
        url: `data:image/svg+xml;charset=UTF-8,${svg}`,
        scaledSize: new window.google.maps.Size(width, height),
        anchor: new window.google.maps.Point(width / 2, height - 2)
    };
};

const getBoundsPath = (bounds: google.maps.LatLngBounds) => {
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    const nw = { lat: ne.lat(), lng: sw.lng() };
    const se = { lat: sw.lat(), lng: ne.lng() };

    return [
        { lat: ne.lat(), lng: ne.lng() },
        se,
        { lat: sw.lat(), lng: sw.lng() },
        nw,
        { lat: ne.lat(), lng: ne.lng() }
    ];
};

const fullscreenButtonStyles = {
    position: 'absolute',
    left: 14,
    bottom: 34,
    zIndex: 10,
    width: 42,
    height: 42,
    background: '#fff',
    color: '#3c4043',
    borderRadius: 1,
    boxShadow: '0 2px 8px rgba(15, 23, 42, 0.22)',
    '&:hover': {
        background: '#f8f9fa'
    }
};

interface LeafletMapProps {
    clubsData?: any[];
    selectedRegion?: string;
    isLoading?: boolean;
}

const LeafletMap = ({ clubsData: clubsDataProp, selectedRegion = '', isLoading = false }: LeafletMapProps) => {
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [selectedPosition, setSelectedPosition] = useState<{ lat: number; lng: number } | null>(null);
    const [activeMarker, setActiveMarker] = useState<string | number | null>(null);
    const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number }>(GREECE_CENTER);
    const [regionBoundaryPath, setRegionBoundaryPath] = useState<{ lat: number; lng: number }[] | null>(null);
    const [isMapReady, setIsMapReady] = useState(false);
    const mapRef = useRef<google.maps.Map | null>(null);
    const mapShellRef = useRef<HTMLDivElement | null>(null);

    const internalQuery = useQuery(
        'geolocation',
        async () => {
            const res = await club.getClubsPublic();
            return Array.isArray(res) ? res : [];
        },
        { suspense: false, enabled: !clubsDataProp }
    );

    const sourceClubsData = useMemo(() => clubsDataProp ?? internalQuery.data ?? [], [clubsDataProp, internalQuery.data]);

    const positions = useMemo(
        () =>
            (sourceClubsData || [])
                .map((clubItem: any) => {
                    const coords = toValidCoordinatePair(clubItem.latitude, clubItem.longitude);
                    if (!coords) return null;

                    return {
                        id: clubItem.id,
                        description: `Club ID: ${clubItem.id}`,
                        lat: coords.lat,
                        lng: coords.lng,
                        popupText: clubItem.title,
                        address1: clubItem.address1,
                        regionName: clubItem.regionName,
                        regionalUnitName: clubItem.regionalUnitName,
                        phone1: clubItem.phone1,
                        phone2: clubItem.phone2,
                        email: clubItem.email
                    };
                })
                .filter(Boolean),
        [sourceClubsData]
    );

    const clubs = useMemo(
        () =>
            positions.map((position: any) => ({
                id: position.id,
                title: position.popupText,
                address1: position.address1,
                description: position.description,
                lat: position.lat,
                lng: position.lng,
                regionName: position.regionName,
                regionalUnitName: position.regionalUnitName,
                phone1: position.phone1,
                phone2: position.phone2,
                email: position.email
            })),
        [positions]
    );

    const fitToPositions = (items: any[]) => {
        if (!mapRef.current || !window.google?.maps || !items.length) return false;

        const bounds = new window.google.maps.LatLngBounds();
        items.forEach((item) => bounds.extend({ lat: item.lat, lng: item.lng }));
        mapRef.current.fitBounds(bounds, 80);
        return true;
    };

    useEffect(() => {
        if (selectedPosition && mapRef.current) {
            mapRef.current.panTo(selectedPosition);
            mapRef.current.setZoom(15);
        }
    }, [selectedPosition]);

    useEffect(() => {
        if (!isMapReady || !mapRef.current || !window.google?.maps) return;

        if (!selectedRegion) {
            setRegionBoundaryPath(null);
            if (!selectedPosition && !fitToPositions(positions)) {
                mapRef.current.setCenter(GREECE_CENTER);
                mapRef.current.setZoom(DEFAULT_GREECE_ZOOM);
            }
            return;
        }

        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ address: `${selectedRegion}, Greece`, region: 'GR' }, (results, status) => {
            const viewport = results?.[0]?.geometry?.viewport;

            if (status === 'OK' && viewport && mapRef.current) {
                setRegionBoundaryPath(getBoundsPath(viewport));
                mapRef.current.fitBounds(viewport, 70);
                return;
            }

            setRegionBoundaryPath(null);
            fitToPositions(positions);
        });
    }, [isMapReady, selectedRegion, positions, selectedPosition]);

    useEffect(() => {
        if (!activeMarker) return;
        const markerStillVisible = positions.some((position: any) => position.id === activeMarker);
        if (!markerStillVisible) {
            setActiveMarker(null);
            setSelectedPosition(null);
        }
    }, [positions, activeMarker]);

    const handleClick = (_title: string, lat: number | any, lng: number | any, id?: string) => {
        const coords = toValidCoordinatePair(lat, lng);

        if (coords) {
            setSelectedPosition(coords);
            setActiveMarker(id || null);
            return;
        }

        setSelectedPosition(null);
        setActiveMarker(null);
        setMapCenter(GREECE_CENTER);

        if (mapRef.current) {
            mapRef.current.setCenter(GREECE_CENTER);
            mapRef.current.setZoom(DEFAULT_GREECE_ZOOM);
        }
    };

    const handleMapLoad = (map: google.maps.Map) => {
        mapRef.current = map;
        setIsMapReady(true);

        if (!fitToPositions(positions)) {
            map.setCenter(GREECE_CENTER);
            map.setZoom(DEFAULT_GREECE_ZOOM);
        }
    };

    const handleMarkerClick = (position: any) => {
        setActiveMarker(position.id);
        setSelectedPosition({ lat: position.lat, lng: position.lng });
    };

    const handleOpenMarkerDetails = (description: any) => {
        const clubId = description.match(/[0-9a-fA-F-]{36}/)?.[0];
        if (!clubId) return;

        navigate(`/public/clubs/manage/${clubId}/details`);
    };

    const handleToggleFullscreen = () => {
        const mapShell = mapShellRef.current as (HTMLDivElement & { webkitRequestFullscreen?: () => Promise<void> }) | null;
        const documentElement = document as Document & { webkitFullscreenElement?: Element; webkitExitFullscreen?: () => Promise<void> };

        if (document.fullscreenElement || documentElement.webkitFullscreenElement) {
            (document.exitFullscreen || documentElement.webkitExitFullscreen)?.call(document);
            return;
        }

        (mapShell?.requestFullscreen || mapShell?.webkitRequestFullscreen)?.call(mapShell);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, height: '100%', width: '100%', background: '#fff' }}>
            <Box
                sx={{
                    width: { xs: '100%', md: 360, lg: 390 },
                    minWidth: { md: 360, lg: 390 },
                    height: { xs: '44%', md: '100%' },
                    minHeight: { xs: 280, md: 0 },
                    overflow: 'hidden',
                    borderRight: { md: '1px solid #d9e3ee' },
                    borderBottom: { xs: '1px solid #d9e3ee', md: 0 },
                    background: '#fff',
                    zIndex: 2
                }}
            >
                <ListOfClubs clubs={clubs} isLoading={isLoading || internalQuery.isLoading} handleClick={(title, lat, lng, id) => handleClick(title, lat, lng, id)} />
            </Box>
            <Box
                ref={mapShellRef}
                sx={{ position: 'relative', flex: 1, height: { xs: '56%', md: '100%' }, minWidth: 0, minHeight: { xs: 340, md: 0 }, background: '#fff' }}
            >
                <Box sx={{ height: '100%', width: '100%' }}>
                    <LoadScript googleMapsApiKey={getGoogleMapsApiKey()} language="el" region="GR">
                        <GoogleMap
                            mapContainerStyle={containerStyle}
                            center={selectedPosition || mapCenter}
                            zoom={selectedPosition ? 15 : DEFAULT_GREECE_ZOOM}
                            onLoad={handleMapLoad}
                            options={{ fullscreenControl: false }}
                        >
                            {regionBoundaryPath && (
                                <Polyline
                                    path={regionBoundaryPath}
                                    options={{
                                        strokeOpacity: 0,
                                        strokeColor: '#1976d2',
                                        icons: [
                                            {
                                                icon: {
                                                    path: 'M 0,-1 0,1',
                                                    strokeOpacity: 1,
                                                    strokeColor: '#1976d2',
                                                    scale: 4
                                                },
                                                offset: '0',
                                                repeat: '18px'
                                            }
                                        ]
                                    }}
                                />
                            )}
                            {positions.map((position: any) => (
                                <Marker
                                    key={position.id}
                                    position={{ lat: position.lat, lng: position.lng }}
                                    icon={activeMarker === position.id ? createSelectedMarkerIcon() : undefined}
                                    onClick={() => handleMarkerClick(position)}
                                >
                                    {activeMarker === position.id && (
                                        <InfoWindow onCloseClick={() => setActiveMarker(null)}>
                                            <div>
                                                <Box
                                                    component="p"
                                                    onClick={() => handleOpenMarkerDetails(position.description)}
                                                    sx={{
                                                        cursor: 'pointer',
                                                        fontWeight: 'bold',
                                                        '&:hover': {
                                                            textDecoration: 'underline'
                                                        }
                                                    }}
                                                >
                                                    {position.popupText}
                                                </Box>
                                                <p>{position.address1}</p>
                                            </div>
                                        </InfoWindow>
                                    )}
                                </Marker>
                            ))}
                        </GoogleMap>
                    </LoadScript>
                </Box>
                {!positions.length && !isLoading && !internalQuery.isLoading && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 24,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            zIndex: 12,
                            background: '#fff',
                            border: '1px solid #d9e3ee',
                            borderRadius: 2,
                            px: 2,
                            py: 1,
                            boxShadow: '0 6px 18px rgba(15,23,42,0.12)'
                        }}
                    >
                        <Typography sx={{ fontWeight: 800 }}>{t('NoClubsOrSchoolsFound')}</Typography>
                    </Box>
                )}
                <IconButton aria-label={t('FullscreenMap')} onClick={handleToggleFullscreen} sx={fullscreenButtonStyles}>
                    <FullscreenIcon />
                </IconButton>
            </Box>
        </Box>
    );
};

export default LeafletMap;
